/**
 * Data provider abstraction. Two implementations: 'edgepacs' (paired
 * image service over its proprietary REST API) and 'dicomweb' (standard
 * QIDO/WADO/STOW). Settings determines which is active; getProvider()
 * returns the right one synchronously after the settings cache has loaded.
 *
 * Note on IDs: EDGEPACS uses integer studyId/seriesId; DICOMweb uses
 * string UIDs (1.2.3.4.5...). To avoid changing studyId/seriesId types
 * across the codebase (used by routing, state keys, annotations, etc.),
 * the DICOMweb provider hashes UIDs to stable 32-bit positive integers
 * and keeps a session map back to the UID for the fetch path.
 */
import { StudyData, SeriesData } from '../cornerstone/init';
import { apiCall, getServerUrl } from '../api/client';
import dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import {
  DicomWebConfig, configFromSettings, qidoStudyToWorklistStudy,
  qidoSeriesToSeriesData, searchStudies as qidoSearchStudies,
  getStudySeries, getSeriesMetadataPage, listSeriesInstanceUIDs,
  buildWadorsImageId, applyAuth,
} from './dicomweb';
import { useDataProgress } from '../stores/dataProgress';
import { extractPdfBlobFromDicom } from '../utils/pdfInstance';
import {
  UserSettings, loadUserSettings, ServerType, DEFAULT_SERVER_TYPE,
} from '../utils/userSettings';

/** Stable djb2 hash → positive 32-bit int. Deterministic across sessions
 *  so the same UID always hashes to the same synthetic id (preserves URL
 *  deep-links and annotation keys). Collisions are ~1 in 2^31 — fine for
 *  per-study scope. */
export function hashUid(uid: string): number {
  let h = 5381;
  for (let i = 0; i < uid.length; i++) {
    h = ((h << 5) + h + uid.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export interface SearchFilters {
  patientName?: string;
  patientId?: string;
  accessionNumber?: string;
  modality?: string;
  studyDateFrom?: string;   // yyyymmdd
  studyDateTo?: string;     // yyyymmdd
  studyDateSpan?: number;
  referringPhysician?: string;
  studyDescription?: string;
  institution?: string;
  patientSex?: string;
  keyImagesOnly?: boolean;
}

export interface DataProvider {
  readonly kind: ServerType;
  /** Run the worklist search. Result rows match WorklistPage's `Study`
   *  shape — patientName, studyDate, etc. as friendly strings. */
  searchStudies(filters: SearchFilters): Promise<any[]>;
  /** Fetch a study + its series. Result matches StudyData. In DICOMweb
   *  mode this is two round-trips (study metadata + series list) plus
   *  one /instances per series the user opens; we defer the per-series
   *  instance fetch to load time to keep initial open fast. */
  getStudy(studyId: number, studyInstanceUID?: string): Promise<StudyData>;
  /** Ensure a series has its sopInstanceUIDs loaded — only needs to do
   *  work in DICOMweb mode. EDGEPACS resolves by integer index so this
   *  is a no-op there. Called by buildImageIds when UIDs are missing. */
  ensureSeriesInstances(study: StudyData, series: SeriesData): Promise<void>;
  /** Build the Cornerstone imageIds for a series. EDGEPACS produces
   *  `wadouri:` URLs (one per index); DICOMweb produces `wadors:` URLs
   *  (one per SOPInstanceUID). */
  buildImageIds(study: StudyData, series: SeriesData, acctoken?: string): string[];
  /** Thumbnail URL for the series — used by SeriesPanel + key-image
   *  rendering. EDGEPACS hits its /thumbnail endpoint; DICOMweb uses
   *  WADO-RS /thumbnail (not all servers support it; renderer should
   *  tolerate 404s gracefully). */
  thumbnailUrl(study: StudyData, series: SeriesData, acctoken?: string): string;
  /** Whether this provider supports a given feature. Drives UI gating:
   *  reports/annotations/key-images exist in EDGEPACS only for now. */
  supports(feature: 'reports' | 'annotations' | 'keyImages' | 'priors' | 'send'): boolean;

  /** Fetch the first instance of a DICOM PDF series, extract the
   *  encapsulated PDF, and return a blob URL the iframe can render.
   *  Caller must `URL.revokeObjectURL(url)` when the viewport unloads
   *  to avoid the blob lingering in memory. */
  getPdfBlobUrl(study: StudyData, series: SeriesData, acctoken?: string): Promise<string>;

  /** Fetch geometric metadata for MPR/3D volume construction. Returns
   *  IPP-derived spacing which is the ONLY reliable Z-spacing for
   *  thin-slice CTs where the DICOM SliceThickness / SpacingBetweenSlices
   *  tags are unreliable. Returns null if the provider doesn't support
   *  this endpoint (DICOMweb servers typically don't). */
  getSeriesGeometry?(
    study: StudyData,
    series: SeriesData,
    acctoken?: string
  ): Promise<SeriesGeometry | null>;

  /** Fetch per-instance geometry for every series in a study, with
   *  instances already sorted by projected Z along each series'
   *  scan-axis normal. Returned instance objects carry the original
   *  `imageIndex` so the caller can build a wadouri imageId from it.
   *
   *  This is the input the volume setup needs to produce a correctly
   *  physically-ordered imageId list for CS3D's spacing calc.
   *
   *  Backend caches the response as gzipped JSON on disk, so repeat
   *  opens are near-instant. Only implemented on EDGEPACS. */
  getStudyGeometry?(
    study: StudyData,
    acctoken?: string
  ): Promise<StudyGeometry | null>;
}

/** Geometric metadata for volume construction, matched to the Delphi
 *  backend's `series.geometry` cmd response. */
export interface SeriesGeometry {
  seriesId: number;
  numImages: number;
  rows: number;
  columns: number;
  /** DICOM PixelSpacing: [rowSpacing, columnSpacing] in mm. */
  pixelSpacing: [number, number];
  /** DICOM (0018,0050). Note: for oversampled thin-slice CTs this is
   *  SMALLER than the actual inter-slice spacing — do NOT use as Z. */
  sliceThickness: number;
  /** DICOM (0018,0088) as read from the header. Often missing or wrong. */
  spacingBetweenSlicesTag: number;
  /** COMPUTED from |lastIPP - firstIPP| / (numImages - 1).
   *  This is the AUTHORITATIVE Z-spacing for volume construction. */
  spacingBetweenSlices: number;
  /** 6-element DICOM (0020,0037): row cosines + column cosines. */
  imageOrientationPatient: [number, number, number, number, number, number];
  firstImagePositionPatient: [number, number, number];
  lastImagePositionPatient:  [number, number, number];
  frameOfReferenceUID: string;
}

/** Per-instance geometry for a single series, sorted by projected Z. */
export interface InstanceGeom {
  /** EDGEPACS row index of this DICOM in the series
   *  (from `ORDER BY image_number_n, image_slicelocation_f`).
   *  Use this to build `wadouri:...?seriesId=X&imageIndex=Y`. */
  imageIndex: number;
  sopInstanceUID: string;
  /** ImagePositionPatient [x, y, z] mm. */
  ipp: [number, number, number];
  sliceThickness: number;
}

/** Geometry for a single series inside a study.geometry response. */
export interface SeriesGeom {
  seriesId: number;
  seriesInstanceUID: string;
  modality: string;
  numImages: number;
  rows?: number;
  columns?: number;
  pixelSpacing?: [number, number];
  imageOrientationPatient?: [number, number, number, number, number, number];
  frameOfReferenceUID?: string;
  /** Sorted by projected Z ascending. Empty for non-imaging series
   *  (SR, DOC, PR, etc). */
  instances: InstanceGeom[];
}

/** Full-study geometry payload — matches Delphi `study.geometry`. */
export interface StudyGeometry {
  studyId: number;
  studyInstanceUID: string;
  numImages: number;
  series: SeriesGeom[];
}

// ───── EDGEPACS provider ────────────────────────────────────────────────

const edgepacs: DataProvider = {
  kind: 'edgepacs',
  async searchStudies(filters) {
    const res = await apiCall<any>('worklist.search', filters as any);
    return res.s === 'ok' ? (res.data || []) : [];
  },
  async getStudy(studyId) {
    const res = await apiCall<StudyData>('study.get', { studyId });
    if (res.s !== 'ok' || !res.data?.[0]) throw new Error(res.error || 'Study not found');
    const study = res.data[0];
    // Defensive — ensure series is always an array so callers can safely
    // .map/.find/.length without crashing on undefined.
    if (!Array.isArray(study.series)) {
      console.warn('[Provider:edgepacs] study.series is not an array, defaulting to []', study);
      study.series = [];
    }
    return study;
  },
  async ensureSeriesInstances() { /* no-op */ },
  buildImageIds(_study, series, acctoken) {
    const ids: string[] = [];
    for (let i = 0; i < series.numImages; i++) {
      ids.push(
        `wadouri:${getServerUrl()}/atlrestapi/v2/dicom?seriesId=${series.seriesId}&imageIndex=${i}&acctoken=${acctoken}`
      );
    }
    return ids;
  },
  thumbnailUrl(_study, series, acctoken) {
    return `${getServerUrl()}/atlrestapi/v2/thumbnail?seriesId=${series.seriesId}&acctoken=${acctoken}`;
  },
  supports() { return true; },  // EDGEPACS supports everything
  async getSeriesGeometry(_study, series) {
    const res = await apiCall<SeriesGeometry>('series.geometry', {
      seriesId: series.seriesId,
    });
    if (res.s !== 'ok' || !res.data?.[0]) return null;
    return res.data[0];
  },
  async getStudyGeometry(study) {
    const res = await apiCall<StudyGeometry>('study.geometry', {
      studyId: study.studyId,
    });
    if (res.s !== 'ok' || !res.data?.[0]) return null;
    return res.data[0];
  },
  async getPdfBlobUrl(_study, series, acctoken) {
    // EDGEPACS serves the raw DICOM file at the same endpoint Cornerstone
    // would have fetched for image rendering. The encapsulated PDF lives
    // inside that file at tag (0042,0011); we parse client-side rather
    // than burden the backend with a separate endpoint.
    const url = `${getServerUrl()}/atlrestapi/v2/dicom?seriesId=${series.seriesId}&imageIndex=0&acctoken=${acctoken}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch DICOM PDF instance (HTTP ${res.status})`);
    const buf = await res.arrayBuffer();
    const blob = extractPdfBlobFromDicom(buf);
    return URL.createObjectURL(blob);
  },
};

// ───── DICOMweb provider ────────────────────────────────────────────────

function dicomwebProvider(cfg: DicomWebConfig): DataProvider {
  return {
    kind: 'dicomweb',
    async searchStudies(filters) {
      // SearchFilters lacks a string index signature; safe to widen here
      // because qidoSearchStudies only reads known optional keys.
      const rows = await qidoSearchStudies(cfg, filters as Record<string, any>);
      return rows.map(qidoStudyToWorklistStudy);
    },
    async getStudy(_studyId, studyInstanceUID) {
      if (!studyInstanceUID) {
        throw new Error('DICOMweb mode needs the StudyInstanceUID — open studies through the worklist.');
      }
      const seriesRows = await getStudySeries(cfg, studyInstanceUID);
      // Server may return null, an object error envelope, or a string body
      // (Orthanc misconfig, IIS reverse-proxy injecting an HTML error page
      // that fetched as JSON returns weird things). Treat any non-array as
      // "no series" so we throw a useful message rather than crashing on
      // .map and leaving the user with the unhelpful default error.
      if (!Array.isArray(seriesRows)) {
        console.warn('[Provider:dicomweb] /series returned non-array, treating as empty', seriesRows);
        throw new Error(
          `DICOMweb server returned non-array response for /studies/${studyInstanceUID}/series. Check the reverse-proxy and PACS endpoint.`
        );
      }
      const series: SeriesData[] = seriesRows.map((row) => {
        const m = qidoSeriesToSeriesData(row);
        return {
          seriesId: hashUid(m.seriesInstanceUID),
          seriesInstanceUID: m.seriesInstanceUID,
          studyInstanceUID,
          seriesNumber: m.seriesNumber,
          seriesDescription: m.seriesDescription,
          modality: m.modality,
          numImages: m.numImages,
          seriesSize: 0,
          bodyPart: m.bodyPart,
          // sopInstanceUIDs populated lazily on first open
        };
      });

      // Pull study-level fields from the first series row (they're repeated
      // across all series in DICOM JSON).
      const first = seriesRows[0] || {};
      const studyMeta = qidoStudyToWorklistStudy(first);
      return {
        studyId: hashUid(studyInstanceUID),
        studyInstanceUID,
        patientName:        studyMeta.patientName,
        patientId:          studyMeta.patientId,
        patientDob:         '',
        patientSex:         studyMeta.patientSex,
        patientAge:         studyMeta.patientAge,
        studyDate:          studyMeta.studyDate,
        studyDescription:   studyMeta.studyDescription,
        accessionNumber:    studyMeta.accessionNumber,
        modality:           studyMeta.modality,
        referringPhysician: studyMeta.referringPhysician,
        institution:        studyMeta.institution,
        numImages: series.reduce((sum, s) => sum + s.numImages, 0),
        series,
      };
    },
    async ensureSeriesInstances(study, series) {
      if (series.sopInstanceUIDs && series.sopInstanceUIDs.length > 0) return;
      if (!study.studyInstanceUID || !series.seriesInstanceUID) return;

      const studyUID = study.studyInstanceUID;
      const seriesUID = series.seriesInstanceUID;

      // Two-phase load to make a big CT viewable as fast as possible:
      //   1. Lightweight /instances → list of SOPInstanceUIDs in order.
      //      Cheap (~200 bytes/row) and tells us the total count so the
      //      viewport's slice slider can show the right max immediately.
      //   2. /metadata in concurrent pages — each page registers its
      //      instances' pixel-format / geometry tags with Cornerstone's
      //      wadors metaDataManager so frames in that page can decode.
      // First page lands quickly → user can scroll and see the first
      // image while later pages are still in flight.
      const instances = await listSeriesInstanceUIDs(cfg, studyUID, seriesUID);
      // Order by InstanceNumber when present; falls back to QIDO's
      // natural order otherwise.
      instances.sort((a, b) => (a.instanceNumber || 0) - (b.instanceNumber || 0));
      const sops = instances.map((i) => i.sopUID);
      series.sopInstanceUIDs = sops;
      series.numImages = sops.length || series.numImages;

      const total = sops.length;
      if (total === 0) return;

      const PAGE_SIZE = 50;
      const MAX_CONCURRENT = 4;
      const progress = useDataProgress.getState();
      progress.start(
        total > PAGE_SIZE
          ? `Loading metadata for ${series.seriesDescription || series.modality}`
          : 'Loading metadata',
        total
      );
      let loaded = 0;

      function register(metadata: any, sop: string) {
        const imageId = buildWadorsImageId(cfg, studyUID, seriesUID, sop, 1);
        try {
          dicomImageLoader.wadors.metaDataManager.add(imageId, metadata);
        } catch {
          (dicomImageLoader as any).wadors?.metaDataProvider?.add?.(imageId, metadata);
        }
      }

      async function fetchPage(offset: number) {
        const rows = await getSeriesMetadataPage(
          cfg, studyUID, seriesUID, PAGE_SIZE, offset
        );
        for (const row of rows) {
          const sop = row['00080018']?.Value?.[0];
          if (sop) register(row, sop);
        }
        loaded += rows.length;
        useDataProgress.getState().bump(loaded);
      }

      // Build the page-offset list, then run with bounded concurrency.
      const offsets: number[] = [];
      for (let o = 0; o < total; o += PAGE_SIZE) offsets.push(o);

      // First page sequentially so we have at least some metadata before
      // anything tries to decode. The remaining pages parallel-fetch.
      try {
        if (offsets.length > 0) await fetchPage(offsets.shift()!);

        // Simple worker-pool — N in flight at once.
        async function worker() {
          while (offsets.length > 0) {
            const o = offsets.shift()!;
            await fetchPage(o);
          }
        }
        await Promise.all(Array.from({ length: MAX_CONCURRENT }, worker));
      } finally {
        useDataProgress.getState().done();
      }
    },
    buildImageIds(study, series) {
      if (!series.sopInstanceUIDs || !study.studyInstanceUID || !series.seriesInstanceUID) {
        return [];  // caller must await ensureSeriesInstances first
      }
      return series.sopInstanceUIDs.map((sop) =>
        buildWadorsImageId(cfg, study.studyInstanceUID!, series.seriesInstanceUID, sop, 1)
      );
    },
    thumbnailUrl(study, series) {
      if (!study.studyInstanceUID || !series.seriesInstanceUID) return '';
      // WADO-RS /thumbnail. Servers that don't implement it 404 — the
      // SeriesPanel's onError handler falls back to a modality placeholder.
      return `${cfg.url}/studies/${study.studyInstanceUID}/series/${series.seriesInstanceUID}/thumbnail`;
    },
    supports(feature) {
      // None of the EDGEPACS-only features have DICOMweb equivalents (yet).
      return feature === 'send' ? false : false;
    },
    async getPdfBlobUrl(study, series) {
      if (!study.studyInstanceUID || !series.seriesInstanceUID) {
        throw new Error('Missing study/series UID for PDF fetch.');
      }
      // Need the first instance's SOPInstanceUID. ensureSeriesInstances
      // populates that as a side effect; for PDF series numImages is 1
      // so the cost is a single QIDO call.
      if (!series.sopInstanceUIDs || series.sopInstanceUIDs.length === 0) {
        const rows = await listSeriesInstanceUIDs(
          cfg, study.studyInstanceUID, series.seriesInstanceUID
        );
        series.sopInstanceUIDs = rows.map((r) => r.sopUID);
      }
      const sop = series.sopInstanceUIDs?.[0];
      if (!sop) throw new Error('No instances found in PDF series.');

      // WADO-RS retrieves a single instance as the original DICOM blob
      // when we Accept application/dicom. Servers that follow the spec
      // return multipart/related; for one instance we can parse the
      // single part's body.
      const url = `${cfg.url}/studies/${study.studyInstanceUID}/series/${series.seriesInstanceUID}/instances/${sop}`;
      const headers: Record<string, string> = {
        Accept: 'multipart/related; type="application/dicom"',
      };
      applyAuth(headers, cfg);
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Failed to fetch DICOM PDF instance (HTTP ${res.status})`);

      // Extract the DICOM bytes from the multipart envelope. Spec calls
      // for boundary in the Content-Type, but in practice we can just
      // find the inner DICOM by locating the double-CRLF that follows
      // the part's headers and stripping the trailing boundary.
      const buf = await res.arrayBuffer();
      const inner = extractMultipartPart(buf);
      const blob = extractPdfBlobFromDicom(inner);
      return URL.createObjectURL(blob);
    },
  };
}

/** Locate the DICOM payload inside a single-part multipart/related
 *  response. The format is roughly:
 *      --boundary\r\nContent-Type: application/dicom\r\n\r\n<DICOM>\r\n--boundary--
 *  We scan for the first `\r\n\r\n` (end of part headers) and slice
 *  through to just before the trailing `\r\n--`. */
function extractMultipartPart(buf: ArrayBuffer): ArrayBuffer {
  const u8 = new Uint8Array(buf);
  // Find \r\n\r\n
  let start = -1;
  for (let i = 0; i < u8.length - 3; i++) {
    if (u8[i] === 13 && u8[i+1] === 10 && u8[i+2] === 13 && u8[i+3] === 10) {
      start = i + 4;
      break;
    }
  }
  if (start < 0) return buf;  // Not multipart — try as raw DICOM
  // Trailing boundary starts with \r\n--
  let end = u8.length;
  for (let i = u8.length - 4; i > start; i--) {
    if (u8[i] === 13 && u8[i+1] === 10 && u8[i+2] === 45 && u8[i+3] === 45) {
      end = i;
      break;
    }
  }
  return u8.slice(start, end).buffer;
}

// ───── Provider resolution ──────────────────────────────────────────────

let cachedProvider: DataProvider = edgepacs;
let cachedSettingsHash = '';
let cachedDicomWebCfg: DicomWebConfig | null = null;

/** Pick the right provider for the current settings. Must be called after
 *  settings have been loaded once (boot). Re-evaluates if settings change.
 *  Synchronous after first load. */
export function getProvider(): DataProvider {
  return cachedProvider;
}

/** Re-resolve the provider against current settings. Uses the in-memory
 *  settings cache by default (cheap — safe to await on every search).
 *  Pass `force: true` after saving new settings to invalidate the cache
 *  and re-read from the server. */
export async function refreshProvider(force = false): Promise<DataProvider> {
  const s: UserSettings = await loadUserSettings(force);
  const hash = JSON.stringify([
    s.serverType, s.dicomwebUrl, s.dicomwebAuth,
    s.dicomwebBasicUser, s.dicomwebBasicPass, s.dicomwebBearerToken,
  ]);
  if (hash === cachedSettingsHash) return cachedProvider;
  cachedSettingsHash = hash;

  const type: ServerType = s.serverType || DEFAULT_SERVER_TYPE;
  if (type === 'dicomweb' && (s.dicomwebUrl || '').trim() !== '') {
    const cfg = configFromSettings(s);
    cachedDicomWebCfg = cfg;
    cachedProvider = dicomwebProvider(cfg);
  } else {
    cachedDicomWebCfg = null;
    cachedProvider = edgepacs;
  }
  return cachedProvider;
}

/** Re-export the auth helper so the Cornerstone wadors loader can build
 *  Authorization headers from the same settings without duplicating logic. */
export function getDicomWebAuthHeaders(): Record<string, string> {
  if (!cachedDicomWebCfg) return {};
  return applyAuth({}, cachedDicomWebCfg);
}
